import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { motivos as motivosApi } from '../services/api';
import {
    ChevronLeft,
    ChevronRight,
    Sun,
    Moon,
    Briefcase,
    Calendar as CalendarIcon,
    Clock,
    AlertCircle,
    Loader2,
    ListFilter,
    X,
    HelpCircle,
    Zap,
    Lock,
    MessageSquare,
    Heart,
    Star,
    UserPlus
} from 'lucide-react';

// Custom Hooks & Components
import { useCalendarLogic } from '../hooks/useCalendarLogic';
import StatCard from '../components/dashboard/StatCard';
import CalendarDay from '../components/dashboard/CalendarDay';
import UserAvisos from '../components/UserAvisos';
import DayEditModal from '../components/DayEditModal';
import PersonalChangesModal from '../components/PersonalChangesModal';
import OnboardingTour from '../components/OnboardingTour';
import { getTurnoForDate } from '../utils/dateUtils';
import { useCustomColors } from '../hooks/useCustomColors';
import ManualPainterPalette from '../components/dashboard/ManualPainterPalette';
import { useHeader } from '../context/HeaderContext';
import { user as userApi } from '../services/api';

const WEEKDAYS = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];

const TOUR_STEPS = [
    { target: '#tour-calendar', title: 'Tu Calendario', content: 'Aquí ves tus turnos (Naranja=M, Azul=T).' },
    { target: '#tour-calendar', title: 'Mantén pulsado', content: 'Presiona 2s un día para pedir un A.P. o Ajuste.' }
];

const UserDashboard = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const toast = useToast();
    const { setHeaderExtra } = useHeader();
    const customColors = useCustomColors();

    const {
        currentMonth,
        currentYear,
        loading,
        error,
        conductor,
        calendarData,
        backendYearStats,
        diasConMotivo,
        notasMes,
        turnos,
        apBalance,
        apSolicitudes,
        submittingAp,
        prevMonth,
        nextMonth,
        loadCalendarData,
        loadApData,
        loadNotasData,
        handleSolicitarAp,
        setCalendarData,
        MONTHS
    } = useCalendarLogic(user, toast);

    const [selectedDay, setSelectedDay] = useState(null);
    const [showDayEditModal, setShowDayEditModal] = useState(false);
    const [showPersonalChangesModal, setShowPersonalChangesModal] = useState(false);
    const [editingDay, setEditingDay] = useState(null);
    const [showActionModal, setShowActionModal] = useState(false);
    const [actionDay, setActionDay] = useState(null);
    const [showTour, setShowTour] = useState(false);
    const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null, type: 'cyan' });

    // Painter Mode States
    const [isPainterMode, setIsPainterMode] = useState(false);
    const [painterRange, setPainterRange] = useState({ start: null, end: null });
    const [showPainterPalette, setShowPainterPalette] = useState(false);
    const [isSavingManual, setIsSavingManual] = useState(false);

    const longPressTimer = useRef(null);
    const isLongPress = useRef(false);

    // Swipe Handling
    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);
    const minSwipeDistance = 50;
    const onTouchStart = (e) => { setTouchEnd(null); setTouchStart(e.targetTouches[0].clientX); };
    const onTouchMove = (e) => setTouchEnd(e.targetTouches[0].clientX);
    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return;
        const distance = touchStart - touchEnd;
        if (distance > minSwipeDistance) { nextMonth(); setSelectedDay(null); }
        else if (distance < -minSwipeDistance) { prevMonth(); setSelectedDay(null); }
    };

    const formatDateStr = (year, month, day) =>
        `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

    // 1. First define calendarDays
    const calendarDays = useMemo(() => {
        if (loading) return [];
        const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
        const firstDay = new Date(currentYear, currentMonth, 1).getDay();
        const startOffset = firstDay === 0 ? 6 : firstDay - 1;
        const days = [];

        for (let i = 0; i < startOffset; i++) days.push({ empty: true, key: `empty-${i}` });

        for (let day = 1; day <= daysInMonth; day++) {
            const dateStr = formatDateStr(currentYear, currentMonth, day);
            const isFestivo = calendarData.festivos.some(f => f.fecha === dateStr);
            const festivoData = calendarData.festivos.find(f => f.fecha === dateStr);
            const shiftOverride = calendarData.shiftOverrides[dateStr];
            const calculatedTurno = getTurnoForDate(dateStr, conductor?.turno);
            const originalTurno = shiftOverride || calculatedTurno;

            const isWorkOverride = ['M', 'T', 'CT'].includes(shiftOverride);
            const isLibranza = calendarData.libranzas.includes(dateStr) && !isWorkOverride;
            const isVacacion = (calendarData.vacaciones.includes(dateStr) || shiftOverride === 'V') && !isWorkOverride;
            const isQuincena = (calendarData.quincenas?.includes(dateStr) || shiftOverride === 'Q') && !isWorkOverride;

            // Buscar en anexo_libranzas (ahora son objetos {fecha, notas})
            const anexoEntry = calendarData.libranzasAnexo?.find(a =>
                typeof a === 'object' ? a.fecha === dateStr : a === dateStr
            );
            const anexoNotas = typeof anexoEntry === 'object' ? anexoEntry?.notas : null;

            // Derivar tipo especial desde las notas del anexo
            const isFiestaCompensada = anexoNotas?.includes('Fiesta compensada') || false;
            const isCompAnosAnt = anexoNotas?.includes('Compensación años anteriores') || false;

            days.push({
                day, dateStr, isLibranza,
                isPartialHoliday: isFestivo && (festivoData?.turnoAfectado || 'ambos') !== 'ambos',
                isFestivo, festivoNombre: festivoData?.nombre, isVacacion, isQuincena,
                isSunday: new Date(currentYear, currentMonth, day).getDay() === 0,
                isToday: dateStr === new Date().toISOString().split('T')[0],
                jornada: calendarData.jornadas.find(j => j.fecha === dateStr),
                motivo: calendarData.motivos?.[dateStr] || diasConMotivo.find(d => d.fecha?.split('T')[0] === dateStr),
                notas: notasMes.filter(n => n.fecha === dateStr),
                isAnexo: !!anexoEntry,
                isFiestaCompensada,
                isCompAnosAnt,
                turno: ((!isLibranza && !isVacacion && !isQuincena) || originalTurno === 'CF') ? originalTurno : null,
                // Painter additions
                isPainterStart: painterRange.start === dateStr,
                isPainterEnd: painterRange.end === dateStr,
                isPainterInRange: isPainterMode && painterRange.start && painterRange.end && 
                                  dateStr >= (painterRange.start < painterRange.end ? painterRange.start : painterRange.end) && 
                                  dateStr <= (painterRange.start < painterRange.end ? painterRange.end : painterRange.start),
                empty: false, key: dateStr
            });
        }
        return days;
    }, [currentYear, currentMonth, calendarData, diasConMotivo, notasMes, conductor?.turno, loading, isPainterMode, painterRange]);

    // 2. Then define monthStats
    const monthStats = useMemo(() => {
        const active = calendarDays.filter(d => !d.empty && !d.isLibranza && !d.isVacacion && !d.isQuincena);
        return {
            total: active.length,
            manana: active.filter(d => d.turno === 'M').length,
            tarde: active.filter(d => d.turno === 'T').length,
            libranzas: calendarDays.filter(d => d.isLibranza).length
        };
    }, [calendarDays]);

    // 3. Now we can define headerContent safely
    const headerContent = useMemo(() => {
        const isManual = conductor?.tipo_calendario === 'manual';
        return (
            <div className="flex items-center gap-2 pointer-events-auto">
                {isManual && (
                    <button
                        onClick={() => {
                            setIsPainterMode(!isPainterMode);
                            setPainterRange({ start: null, end: null });
                            setSelectedDay(null);
                        }}
                        className={`group relative flex items-center justify-center p-2.5 rounded-xl border transition-all ${
                            isPainterMode 
                                ? 'bg-cyan-500 border-cyan-400 text-white shadow-lg shadow-cyan-500/25 ring-2 ring-cyan-500/20' 
                                : 'bg-white/5 border-white/10 text-gray-400 hover:bg-white/10 hover:border-white/20'
                        }`}
                        title={isPainterMode ? "Desactivar modo pintar" : "Activar modo pintar"}
                    >
                        <Zap className={`w-5 h-5 transition-transform duration-300 ${isPainterMode ? 'scale-110' : 'group-hover:rotate-12'}`} />
                        {isPainterMode && (
                            <motion.span 
                                layoutId="painter-glow" 
                                className="absolute -inset-1 rounded-xl bg-cyan-400/20 blur-md -z-10"
                            />
                        )}
                    </button>
                )}
                <button
                    onClick={() => setShowTour(true)}
                    className="p-2.5 bg-white/5 rounded-xl border border-white/10 hover:bg-white/10 transition-colors"
                    title="Ayuda"
                >
                    <HelpCircle className="w-5 h-5 text-gray-400" />
                </button>
            </div>
        );
    }, [conductor?.tipo_calendario, isPainterMode]);

    // 4. Finally update Layout Header
    useEffect(() => {
        setHeaderExtra(headerContent);
        return () => {
            // Use a small check to avoid flickering during local state changes
            // Only clear if component is actually unmounting or data is gone
        };
    }, [headerContent, setHeaderExtra]);

    const handleLongPressStart = (dayData) => {
        isLongPress.current = false;
        longPressTimer.current = setTimeout(() => {
            isLongPress.current = true;
            if (navigator.vibrate) navigator.vibrate(50);
            setActionDay(dayData);
            setShowActionModal(true);
        }, 800);
    };

    const handleLongPressEnd = () => { if (longPressTimer.current) clearTimeout(longPressTimer.current); };

    const handleDayClick = (dayData) => {
        if (isLongPress.current) { isLongPress.current = false; return; }
        
        if (isPainterMode) {
            if (!painterRange.start || (painterRange.start && painterRange.end)) {
                // First click or resetting after full range
                setPainterRange({ start: dayData.dateStr, end: null });
            } else {
                // Second click: set end and open palette
                setPainterRange(prev => ({ ...prev, end: dayData.dateStr }));
                setShowPainterPalette(true);
            }
            return;
        }

        setSelectedDay(dayData);
    };

    const handlePainterSelect = async (turnoId) => {
        if (!painterRange.start || !painterRange.end) return;
        
        // Robust date range calculation (using string split to avoid TZ issues)
        const [sYear, sMonth, sDay] = painterRange.start.split('-').map(Number);
        const [eYear, eMonth, eDay] = painterRange.end.split('-').map(Number);
        
        const d1 = new Date(sYear, sMonth - 1, sDay);
        const d2 = new Date(eYear, eMonth - 1, eDay);
        
        const start = d1 < d2 ? d1 : d2;
        const end = d1 < d2 ? d2 : d1;
        
        const dates = [];
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            dates.push(`${y}-${m}-${day}`);
        }

        // Map turnoId to a display code for immediate feedback
        const shiftCodes = { 1: 'M', 2: 'T', 5: 'V', 6: 'L', 23: 'P' };
        const code = shiftCodes[turnoId] || 'M';

        // Update local state immediately (Optimistic)
        setCalendarData(prev => {
            if (!prev) return prev;
            const nextOverrides = { ...prev.shiftOverrides };
            const nextLibranzas = new Set(prev.libranzas || []);
            const nextVacaciones = new Set(prev.vacaciones || []);

            dates.forEach(date => {
                nextOverrides[date] = code;
                if (code === 'L') {
                    nextLibranzas.add(date);
                    nextVacaciones.delete(date);
                } else if (code === 'V') {
                    nextVacaciones.add(date);
                    nextLibranzas.delete(date);
                } else {
                    nextLibranzas.delete(date);
                    nextVacaciones.delete(date);
                }
            });

            return {
                ...prev,
                shiftOverrides: nextOverrides,
                libranzas: Array.from(nextLibranzas),
                vacaciones: Array.from(nextVacaciones)
            };
        });

        // Backend save
        setIsSavingManual(true);
        try {
            await userApi.saveManualBatch({ fechas: dates, turno_id: turnoId });
            toast.show('success', `${dates.length} días actualizados correctamente`);
            // Background sync (background: true if supported, or just wait)
            await loadCalendarData();
        } catch (error) {
            console.error('Error saving manual range:', error);
            toast.show('error', 'Error al guardar los cambios en el servidor');
            await loadCalendarData();
        } finally {
            setIsSavingManual(false);
            setShowPainterPalette(false);
            setPainterRange({ start: null, end: null });
        }
    };

    // Sync selectedDay with updated calendarDays (after edit/load)
    useEffect(() => {
        if (selectedDay) {
            const updated = calendarDays.find(d => d.dateStr === selectedDay.dateStr);
            if (updated) setSelectedDay(updated);
        }
    }, [calendarDays]);

    const handleConfirmQuickAction = (dateStr, tipo) => {
        const isAjuste = tipo === 'ajuste_jornada';
        setConfirmModal({
            isOpen: true,
            title: isAjuste ? 'Solicitar Ajuste' : 'Solicitar A.P.',
            message: `¿Estás seguro de solicitar un ${isAjuste ? 'AJUSTE DE JORNADA' : 'ASUNTO PROPIO (A.P.)'} para el día ${actionDay.day}?`,
            type: isAjuste ? 'amber' : 'cyan',
            onConfirm: async () => {
                await handleSolicitarAp(dateStr, tipo);
                setShowActionModal(false);
                setConfirmModal(prev => ({ ...prev, isOpen: false }));
            }
        });
    };

    // Helper: resolve color → user custom > admin turno > CSS var fallback
    const getColor = (turnoName, cssVarFallback) => {
        // 1. User custom color (highest priority)
        if (customColors[turnoName]) return customColors[turnoName];
        // 2. Admin turno color from DB
        const adminTurno = turnos.find(t => t.nombre === turnoName);
        if (adminTurno?.color_bg) return adminTurno.color_bg;
        // 3. CSS variable fallback
        return cssVarFallback;
    };

    const getDayStyle = (dayData) => {
        if (dayData.empty) return '';
        const base = 'h-full w-full rounded-xl text-sm font-bold transition-all flex flex-col items-center justify-center border border-transparent overflow-hidden';

        // Estilos para motivos parciales (Reunión/Asamblea)
        const nombreMotivo = dayData.motivo?.motivo_nombre?.toUpperCase() || dayData.motivo?.nombre?.toUpperCase() || "";
        const esParcial = dayData.motivo && (
            dayData.motivo.tipo_hora_sindical === 'reunion_sindical' || 
            dayData.motivo.tipo_hora_sindical === 'asamblea' ||
            nombreMotivo.includes('REUNIÓN SINDICAL') ||
            nombreMotivo.includes('ASAMBLEA')
        );
        
        if (esParcial) {
            const colorMotivo = dayData.motivo.motivo_color || '#8b5cf6';
            
            // Determinar color base (Vacaciones > Libranza > Quincena > Turno)
            let colorBase = 'rgba(255, 255, 255, 0.1)';
            if (dayData.isVacacion) colorBase = getColor('Vacaciones', '#ef4444');
            else if (dayData.isLibranza) colorBase = getColor('Libranza', '#22c55e');
            else if (dayData.isQuincena) colorBase = getColor('Quincena', '#10b981');
            else if (dayData.turno === 'M') colorBase = getColor('Mañana', '#f97316');
            else if (dayData.turno === 'T') colorBase = getColor('Tarde', '#3b82f6');
            else if (dayData.turno) {
                const genericTurno = turnos.find(t => t.nombre.startsWith(dayData.turno));
                colorBase = genericTurno?.color_bg || '#3b82f6';
            }

            return {
                className: base,
                style: {
                    background: `linear-gradient(135deg, ${colorBase} 50%, ${colorMotivo} 50%)`,
                    color: 'white',
                    boxShadow: `0 4px 12px ${colorMotivo}40`
                }
            };
        }

        if (dayData.motivo?.motivo_id) {
            return { className: base, style: { background: dayData.motivo.motivo_color, color: 'white' } };
        }

        if (dayData.isVacacion) {
            const bg = getColor('Vacaciones', 'var(--day-vacation-bg)');
            const isCustom = bg !== 'var(--day-vacation-bg)';
            return {
                className: base,
                style: {
                    background: bg,
                    color: isCustom ? '#ffffff' : 'var(--day-vacation-text)',
                    border: isCustom ? `1px solid ${bg}` : '1px solid var(--day-vacation-border)'
                }
            };
        }

        if (dayData.isQuincena) {
            const bg = getColor('Quincena', '#10b981');
            return {
                className: base,
                style: {
                    background: bg,
                    color: '#ffffff',
                    border: `1px solid ${bg}`
                }
            };
        }

        // Fiesta Compensada → morado/rosa
        if (dayData.isFiestaCompensada) {
            const bg = getColor('CF', 'linear-gradient(135deg, #a855f7, #7c3aed)');
            return {
                className: base,
                style: {
                    background: bg,
                    color: '#ffffff',
                    border: bg.startsWith('linear') ? '1px solid #9333ea' : `1px solid ${bg}`
                }
            };
        }

        // Compensación Años Anteriores → ámbar
        if (dayData.isCompAnosAnt) {
            return {
                className: base,
                style: {
                    background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                    color: '#ffffff',
                    border: '1px solid #b45309'
                }
            };
        }

        if (dayData.isLibranza) {
            const bg = getColor('Libranza', 'var(--day-libranza-bg)');
            const isCustom = bg !== 'var(--day-libranza-bg)';
            return {
                className: base,
                style: {
                    background: bg,
                    color: isCustom ? '#ffffff' : 'var(--day-libranza-text)',
                    border: isCustom ? `1px solid ${bg}` : '1px solid var(--day-libranza-border)'
                }
            };
        }

        if (dayData.turno === 'M') {
            const bg = getColor('Mañana', 'var(--day-morning-bg)');
            const isCustom = bg !== 'var(--day-morning-bg)';
            return {
                className: base,
                style: {
                    background: bg,
                    color: isCustom ? '#ffffff' : 'var(--day-morning-text)',
                    border: isCustom ? `1px solid ${bg}` : '1px solid var(--day-morning-border)'
                }
            };
        }

        if (dayData.turno === 'T') {
            const bg = getColor('Tarde', 'var(--day-afternoon-bg)');
            const isCustom = bg !== 'var(--day-afternoon-bg)';
            return {
                className: base,
                style: {
                    background: bg,
                    color: isCustom ? '#ffffff' : 'var(--day-afternoon-text)',
                    border: isCustom ? `1px solid ${bg}` : '1px solid var(--day-afternoon-border)'
                }
            };
        }

        const turnoData = turnos.find(t => t.nombre.startsWith(dayData.turno || 'M'));
        const customGeneric = turnoData ? customColors[turnoData.nombre] : null;

        return {
            className: base,
            style: {
                background: customGeneric || `linear-gradient(135deg, ${turnoData?.color_bg || '#3b82f6'}, ${turnoData?.color_bg_secondary || turnoData?.color_bg || '#1d4ed8'})`,
                color: 'white'
            }
        };
    };

    const getDayLabelText = (day) => {
        if (!day) return '';
        if (day.motivo?.motivo_nombre) return day.motivo.motivo_nombre;
        if (day.isFiestaCompensada) return 'Fiesta Compensada';
        if (day.isCompAnosAnt) return 'Compensación Años Anteriores';
        if (day.isLibranza) return 'Libranza';
        if (day.isQuincena) return 'Quincena';
        if (day.isVacacion) return 'Vacaciones';
        return `Turno ${day.turno}`;
    };

    if (loading) return (
        <div className="flex flex-col items-center justify-center p-20">
            <Loader2 className="w-10 h-10 text-cyan-400 animate-spin" />
            <p className="mt-4 text-gray-500 font-medium animate-pulse tracking-widest text-xs uppercase">Sincronizando...</p>
        </div>
    );

    if (error === 'noLink') return null;

    return (
        <div
            className="flex flex-col transition-all duration-500 md:gap-4 pb-24 md:pb-0 select-none"
            onContextMenu={(e) => e.preventDefault()}
        >
            <div className="grid lg:grid-cols-3 gap-0 md:gap-4 h-full">
                <div className="lg:col-span-2 flex flex-col">
                    {/* Modern Calendar Card - Full viewport on mobile */}
                    <motion.div
                        id="tour-calendar"
                        onTouchStart={onTouchStart}
                        onTouchMove={onTouchMove}
                        onTouchEnd={onTouchEnd}
                        className={`bg-[var(--glass-bg)] border border-[var(--glass-border)] md:rounded-3xl overflow-hidden backdrop-blur-xl flex flex-col calendar-container-mobile md:h-auto ${selectedDay ? 'calendar-with-panel' : ''}`}
                    >
                        <div className="p-5 flex flex-col gap-5 border-b border-[var(--glass-border)]">
                            {/* Fila 1: Título y Botón Cambios */}
                            <div className="flex items-center justify-between">
                                <h2 className="text-2xl font-black text-[var(--text-primary)]">
                                    {MONTHS[currentMonth]} <span className="text-[var(--text-muted)] font-normal">{currentYear}</span>
                                </h2>
                                <button
                                    onClick={() => setShowPersonalChangesModal(true)}
                                    className="px-4 py-2 bg-indigo-500/20 text-indigo-400 rounded-xl border border-indigo-500/20 hover:bg-indigo-500/30 transition-all flex items-center gap-2 text-[10px] font-black uppercase tracking-widest"
                                    title="Ver mis modificaciones"
                                >
                                    <ListFilter className="w-4 h-4" />
                                    Cambios
                                </button>
                            </div>

                            {/* Fila 2: Estadísticas y Selector de Mes */}
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3 bg-[var(--glass-bg)] p-2 px-4 rounded-2xl border border-[var(--glass-border)]">
                                    <div className="flex items-center gap-2 pr-3 border-r border-[var(--glass-border)]">
                                        <Sun className="w-4 h-4 text-amber-500" />
                                        <span className="text-sm font-black text-[var(--text-primary)]">{monthStats.manana}</span>
                                    </div>
                                    <div className="flex items-center gap-2 pr-3 border-r border-[var(--glass-border)]">
                                        <Moon className="w-4 h-4 text-indigo-400" />
                                        <span className="text-sm font-black text-[var(--text-primary)]">{monthStats.tarde}</span>
                                    </div>
                                    <div className="flex flex-col items-center">
                                        <span className="text-sm font-black text-cyan-400">{backendYearStats?.totalWorkDays || 0}</span>
                                        <span className="text-[7px] font-black uppercase tracking-tighter text-[var(--text-muted)] -mt-1">ANUAL</span>
                                    </div>
                                </div>

                                <div className="flex gap-1 bg-[var(--glass-bg)] p-1 rounded-2xl border border-[var(--glass-border)]">
                                    <button
                                        onClick={() => { prevMonth(); setSelectedDay(null); }}
                                        className="p-2 hover:bg-[var(--glass-bg)] rounded-xl transition-colors"
                                    >
                                        <ChevronLeft className="w-6 h-6 text-[var(--text-primary)]" />
                                    </button>
                                    <button
                                        onClick={() => { nextMonth(); setSelectedDay(null); }}
                                        className="p-2 hover:bg-[var(--glass-bg)] rounded-xl transition-colors"
                                    >
                                        <ChevronRight className="w-6 h-6 text-[var(--text-primary)]" />
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="grid grid-cols-7 text-center p-1 bg-[var(--glass-bg)] border-b border-[var(--glass-border)]">
                            {WEEKDAYS.map(w => <div key={w} className="text-[10px] font-black text-[var(--text-muted)] py-3 uppercase tracking-tighter">{w}</div>)}
                        </div>
                        <div className="grid grid-cols-7 gap-1 p-2 md:gap-1.5 md:p-3 md:min-h-[480px] flex-1 calendar-grid-fill">
                            {calendarDays.map(day => (
                                <CalendarDay
                                    key={day.key}
                                    dayData={day}
                                    isManualMode={conductor?.tipo_calendario === 'manual'}
                                    isSelected={selectedDay?.dateStr === day.dateStr}
                                    isAnySelected={!!selectedDay}
                                    onClick={handleDayClick}
                                    onLongPressStart={handleLongPressStart}
                                    onLongPressEnd={handleLongPressEnd}
                                    getDayStyle={getDayStyle}
                                    getApStatusForDay={(date) => apSolicitudes.find(s => s.fecha?.startsWith(date))}
                                    shiftOverrides={calendarData.shiftOverrides}
                                />
                            ))}
                        </div>
                    </motion.div>
                </div>

                {/* Info Panel Desktop */}
                <div className="hidden lg:flex flex-col gap-4">
                    {selectedDay && (
                        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="bg-[var(--bg-secondary)] border border-[var(--glass-border)] rounded-3xl p-6 shadow-2xl">
                            <div className="text-center mb-6">
                                <p className="text-5xl font-black text-cyan-400">{selectedDay.day}</p>
                                <p className="text-sm font-bold text-[var(--text-muted)] uppercase tracking-widest">{MONTHS[currentMonth]}</p>
                            </div>
                            <div className="space-y-3">
                                <div className="bg-[var(--glass-bg)] p-4 rounded-2xl border border-[var(--glass-border)] text-center">
                                    <p className="text-xs font-black uppercase tracking-widest text-cyan-500">{getDayLabelText(selectedDay)}</p>
                                </div>

                                {selectedDay.isFestivo && (
                                    <div className="bg-yellow-500/10 p-4 rounded-2xl border border-yellow-500/20 text-center">
                                        <p className="text-[10px] font-black uppercase tracking-widest text-yellow-500 mb-1">Festivo:</p>
                                        <p className="text-sm font-bold text-yellow-400">{selectedDay.festivoNombre || 'Festividad'}</p>
                                    </div>
                                )}

                                {selectedDay.notas && selectedDay.notas.length > 0 && (
                                    <div className="bg-[var(--glass-bg)] p-4 rounded-2xl border-[var(--glass-border)] space-y-3">
                                        <p className="text-[10px] font-black uppercase text-[var(--text-muted)] mb-1 tracking-widest flex items-center gap-2">
                                            <MessageSquare className="w-3 h-3" /> Notas:
                                        </p>
                                        <div className="space-y-2">
                                            {selectedDay.notas.map(nota => {
                                                const config = {
                                                    medico: { icon: Heart, color: 'text-red-400', border: 'border-l-red-500' },
                                                    laboral: { icon: Briefcase, color: 'text-amber-400', border: 'border-l-amber-500' },
                                                    personal: { icon: Star, color: 'text-purple-400', border: 'border-l-purple-500' },
                                                    general: { icon: MessageSquare, color: 'text-cyan-400', border: 'border-l-cyan-500' }
                                                }[nota.tipo] || { icon: MessageSquare, color: 'text-cyan-400', border: 'border-l-cyan-500' };
                                                const NoteIcon = config.icon;
                                                
                                                return (
                                                    <div key={nota.id} className={`bg-white/5 p-3 rounded-xl border-l-4 ${config.border} border-white/5`}>
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <NoteIcon className={`w-3 h-3 ${config.color}`} />
                                                            <span className={`text-[9px] font-black uppercase tracking-tighter ${config.color}`}>{nota.tipo || 'General'}</span>
                                                        </div>
                                                        <p className="text-sm text-[var(--text-primary)] font-medium leading-relaxed italic">
                                                            "{nota.comentario}"
                                                        </p>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {selectedDay.jornada && !selectedDay.isVacacion && !selectedDay.isQuincena && (
                                    <div className="bg-cyan-500/10 p-4 rounded-2xl border border-cyan-500/20">
                                        <p className="text-[10px] font-black uppercase text-cyan-500 mb-1 tracking-widest">Servicio:</p>
                                        <p className="text-sm font-bold text-[var(--text-primary)]">
                                            Línea {selectedDay.jornada.linea} - {selectedDay.jornada.servicio}
                                            {selectedDay.jornada.numero_bus && (
                                                <span className="ml-2 px-2 py-0.5 rounded bg-cyan-500/20 text-cyan-400 text-[10px] border border-cyan-500/30">
                                                    BUS {selectedDay.jornada.numero_bus}
                                                </span>
                                            )}
                                        </p>
                                    </div>
                                )}

                                {apSolicitudes.find(s => s.fecha?.startsWith(selectedDay.dateStr)) && (
                                    <div className={`p-4 rounded-2xl border ${apSolicitudes.find(s => s.fecha?.startsWith(selectedDay.dateStr)).estado === 'concedido' ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' :
                                        apSolicitudes.find(s => s.fecha?.startsWith(selectedDay.dateStr)).estado === 'rechazado' ? 'bg-red-500/10 border-red-500/20 text-red-400' :
                                            'bg-amber-500/10 border-amber-500/20 text-amber-400'
                                        }`}>
                                        <p className="text-[10px] font-black uppercase mb-1 tracking-widest">Estado Solicitud:</p>
                                        <p className="text-sm font-bold uppercase">
                                            {apSolicitudes.find(s => s.fecha?.startsWith(selectedDay.dateStr)).tipo === 'ap' ? 'Asunto Propio' : 'Ajuste Jornada'} - {apSolicitudes.find(s => s.fecha?.startsWith(selectedDay.dateStr)).estado}
                                        </p>
                                    </div>
                                )}

                                { (calendarData.exchangeInfo?.[selectedDay.dateStr] || ['CT', 'CF'].includes(calendarData.shiftOverrides?.[selectedDay.dateStr])) && (
                                    (() => {
                                        const override = calendarData.shiftOverrides?.[selectedDay.dateStr];
                                        const exInfo = calendarData.exchangeInfo?.[selectedDay.dateStr];
                                        const exType = exInfo ? exInfo.tipo : (override === 'CT' ? 'CT' : 'CF');
                                        const exPartner = exInfo ? exInfo.partner : '(Sin especificar)';
                                        
                                        return (
                                            <div className="bg-indigo-500/10 p-4 rounded-2xl border border-indigo-500/20">
                                                <p className="text-[10px] font-black uppercase text-indigo-500 mb-1 tracking-widest">Intercambio de día:</p>
                                                <div className="flex items-center gap-2">
                                                    <div className="w-8 h-8 rounded-full bg-indigo-500/20 flex items-center justify-center text-indigo-400">
                                                        <UserPlus className="w-4 h-4" />
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-bold text-[var(--text-primary)]">
                                                            {exType === 'CT' ? 'Trabajas para' : 'Libra por'}
                                                        </p>
                                                        <p className="text-xs text-indigo-400 font-black uppercase">{exPartner}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })()
                                )}

                                <button onClick={() => { setEditingDay(selectedDay); setShowDayEditModal(true); }} className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 py-4 rounded-2xl font-black text-white text-sm transition-all shadow-lg shadow-orange-500/20 uppercase tracking-widest active:scale-[0.98]">Editar Día</button>
                            </div>
                        </motion.div>
                    )}
                    <UserAvisos />
                </div>
            </div>

            {/* Mobile Bottom Bar - Optimized for comments */}
            <AnimatePresence>
                {selectedDay && (
                    <motion.div
                        initial={{ y: 80, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 80, opacity: 0 }}
                        className="lg:hidden fixed bottom-24 left-4 right-4 bg-[var(--bg-secondary)] border border-[var(--glass-border)] p-5 rounded-[32px] flex flex-col gap-3 shadow-2xl z-50 shadow-black/10"
                    >
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div className="text-3xl font-black text-orange-500">{selectedDay.day}</div>
                                <div>
                                    <p className="text-[10px] font-black uppercase text-[var(--text-muted)] tracking-widest">{MONTHS[currentMonth].slice(0, 3)}</p>
                                    <p className="text-sm font-black text-[var(--text-primary)] uppercase">{getDayLabelText(selectedDay)}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <button onClick={() => { setEditingDay(selectedDay); setShowDayEditModal(true); }} className="bg-gradient-to-r from-orange-500 to-amber-500 px-5 py-2.5 rounded-2xl text-[10px] font-black uppercase tracking-widest text-white shadow-lg shadow-orange-500/20 text-white active:scale-95 transition-transform">Editar</button>
                                <button onClick={() => setSelectedDay(null)} className="p-2.5 bg-[var(--glass-bg)] rounded-xl border border-[var(--glass-border)] text-[var(--text-muted)]">
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                        </div>
                        {/* More interior replacements for mobile panel */}
                        {( (selectedDay.notas && selectedDay.notas.length > 0) || selectedDay.jornada || selectedDay.isFestivo || apSolicitudes.some(s => s.fecha?.substring(0, 10) === selectedDay.dateStr) || calendarData.exchangeInfo?.[selectedDay.dateStr] || ['CT', 'CF'].includes(calendarData.shiftOverrides?.[selectedDay.dateStr])) && (
                            <div className="pt-3 border-t border-[var(--glass-border)] space-y-2.5">
                                {selectedDay.isFestivo && (
                                    <p className="text-[10px] font-bold text-yellow-500 uppercase tracking-widest flex items-center gap-2">
                                        ⭐ Festivo: {selectedDay.festivoNombre || 'Festividad'}
                                    </p>
                                )}
                                {selectedDay.notas && selectedDay.notas.length > 0 && (
                                    <div className="space-y-2">
                                        {selectedDay.notas.map(nota => {
                                            const config = {
                                                medico: { icon: Heart, color: 'text-red-400', bg: 'bg-red-500/10' },
                                                laboral: { icon: Briefcase, color: 'text-amber-400', bg: 'bg-amber-500/10' },
                                                personal: { icon: Star, color: 'text-purple-400', bg: 'bg-purple-500/10' },
                                                general: { icon: MessageSquare, color: 'text-cyan-400', bg: 'bg-cyan-500/10' }
                                            }[nota.tipo] || { icon: MessageSquare, color: 'text-cyan-400', bg: 'bg-cyan-500/10' };
                                            const NoteIcon = config.icon;

                                            return (
                                                <div key={nota.id} className={`flex items-start gap-3 p-3 rounded-2xl border border-[var(--glass-border)] ${config.bg}`}>
                                                    <NoteIcon className={`w-4 h-4 mt-0.5 ${config.color}`} />
                                                    <div className="flex-1">
                                                        <p className="text-[8px] font-black uppercase tracking-widest opacity-60 mb-0.5">{nota.tipo || 'General'}</p>
                                                        <p className="text-xs text-[var(--text-primary)] font-medium italic">
                                                            {nota.comentario}
                                                        </p>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                                {selectedDay.jornada && !selectedDay.isVacacion && !selectedDay.isQuincena && (
                                    <p className="text-[10px] font-bold text-cyan-400 uppercase tracking-widest flex items-center gap-2">
                                        🚌 Línea {selectedDay.jornada.linea} • Serv. {selectedDay.jornada.servicio}
                                        {selectedDay.jornada.numero_bus && ` • Bus ${selectedDay.jornada.numero_bus}`}
                                    </p>
                                )}
                                {apSolicitudes.find(s => s.fecha?.substring(0, 10) === selectedDay.dateStr) && (
                                    <p className={`text-[10px] font-black uppercase tracking-widest flex items-center gap-2 ${apSolicitudes.find(s => s.fecha?.substring(0, 10) === selectedDay.dateStr).estado === 'concedido' ? 'text-emerald-400' :
                                        apSolicitudes.find(s => s.fecha?.substring(0, 10) === selectedDay.dateStr).estado === 'rechazado' ? 'text-red-400' :
                                            'text-amber-400'
                                        }`}>
                                        ⏳ {apSolicitudes.find(s => s.fecha?.substring(0, 10) === selectedDay.dateStr).tipo === 'ap' ? 'A.P.' : 'AJUSTE'} - {apSolicitudes.find(s => s.fecha?.substring(0, 10) === selectedDay.dateStr).estado}
                                    </p>
                                )}
                                { (calendarData.exchangeInfo?.[selectedDay.dateStr] || ['CT', 'CF'].includes(calendarData.shiftOverrides?.[selectedDay.dateStr])) && (
                                    (() => {
                                        const override = calendarData.shiftOverrides?.[selectedDay.dateStr];
                                        const exInfo = calendarData.exchangeInfo?.[selectedDay.dateStr];
                                        const exType = exInfo ? exInfo.tipo : (override === 'CT' ? 'CT' : 'CF');
                                        const exPartner = exInfo ? exInfo.partner : '(Sin especificar)';
                                        return (
                                            <p className="text-[10px] font-bold text-indigo-400 uppercase tracking-widest flex items-center gap-2">
                                                🔄 {exType === 'CT' ? 'Trabajas para' : 'Libra por'} {exPartner}
                                            </p>
                                        );
                                    })()
                                )}
                            </div>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Modals */}
            <DayEditModal
                isOpen={showDayEditModal}
                onClose={() => { setShowDayEditModal(false); setEditingDay(null); }}
                dayData={editingDay}
                conductorId={conductor?.id}
                existingMarca={editingDay?.motivo}
                existingJornada={editingDay?.jornada}
                festivos={calendarData?.festivos?.map(f => f.fecha) || []}
                exchangeInfo={editingDay ? calendarData?.exchangeInfo?.[editingDay.dateStr] : null}
                shiftOverride={editingDay ? calendarData?.shiftOverrides?.[editingDay.dateStr] : null}
                onSave={loadCalendarData}
                onNotesUpdated={loadNotasData}
            />
            <PersonalChangesModal isOpen={showPersonalChangesModal} onClose={() => setShowPersonalChangesModal(false)} changes={diasConMotivo} turnos={turnos} onDelete={async (id) => { await motivosApi.eliminarDia(id); loadCalendarData(); }} />

            {/* Quick Actions Drawer */}
            <AnimatePresence>
                {showActionModal && actionDay && (
                    <div className="fixed inset-0 z-[100] flex items-end justify-center p-4">
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowActionModal(false)} />
                        <motion.div initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }} className="bg-[var(--bg-secondary)] w-full max-w-md rounded-t-[40px] p-10 relative z-10 border-t border-[var(--glass-border)] shadow-2xl">
                            <div className="w-12 h-1.5 bg-[var(--glass-border)] rounded-full mx-auto mb-8" />
                            <h3 className="text-2xl font-black text-[var(--text-primary)] mb-1">Acciones Rápidas</h3>
                            <p className="text-sm text-[var(--text-muted)] mb-8 font-medium">Solicitud para el {actionDay.day} de {MONTHS[currentMonth]}</p>
                            <div className="space-y-4">
                                {actionDay.isLibranza || actionDay.isVacacion || actionDay.isQuincena || actionDay.isBaja || actionDay.turno === 'CF' ? (
                                    <button disabled className="w-full bg-slate-800/30 border border-slate-700/30 p-6 rounded-3xl text-left flex justify-between items-center opacity-50 cursor-not-allowed select-none">
                                        <div>
                                            <span className="font-black text-slate-500 text-sm uppercase tracking-widest block">Asunto Propio (A.P.)</span>
                                            <span className="text-[10px] text-red-400 font-bold uppercase tracking-wider mt-1 block">
                                                No disponible en {actionDay.isLibranza ? 'Libranza' : actionDay.isVacacion ? 'Vacaciones' : actionDay.isQuincena ? 'Quincena' : 'este día'}
                                            </span>
                                        </div>
                                        <Lock className="w-5 h-5 text-slate-600" />
                                    </button>
                                ) : (apBalance?.desglose?.ap?.disponibles <= 0) ? (
                                    <button disabled className="w-full bg-slate-800/30 border border-slate-700/30 p-6 rounded-3xl text-left flex justify-between items-center opacity-50 cursor-not-allowed select-none">
                                        <div>
                                            <span className="font-black text-slate-500 text-sm uppercase tracking-widest block">Asunto Propio (A.P.)</span>
                                            <span className="text-[10px] text-amber-500 font-bold uppercase tracking-wider mt-1 block">
                                                Sin saldo disponible
                                            </span>
                                        </div>
                                        <AlertCircle className="w-5 h-5 text-amber-600" />
                                    </button>
                                ) : (
                                    <button onClick={() => handleConfirmQuickAction(actionDay.dateStr, 'ap')} className="w-full bg-cyan-500/10 border border-cyan-500/20 p-6 rounded-3xl text-left flex justify-between items-center group transition-all hover:bg-cyan-500/20">
                                        <span className="font-black text-cyan-400 text-sm uppercase tracking-widest">Asunto Propio (A.P.)</span>
                                        <ChevronRight className="w-5 h-5 text-cyan-400 group-hover:translate-x-1 transition-transform" />
                                    </button>
                                )}

                                {actionDay.isLibranza || actionDay.isVacacion || actionDay.isQuincena || actionDay.isBaja || actionDay.turno === 'CF' ? (
                                    <button disabled className="w-full bg-slate-800/30 border border-slate-700/30 p-6 rounded-3xl text-left flex justify-between items-center opacity-50 cursor-not-allowed select-none">
                                        <div>
                                            <span className="font-black text-slate-500 text-sm uppercase tracking-widest block">Ajuste de Jornada</span>
                                            <span className="text-[10px] text-red-400 font-bold uppercase tracking-wider mt-1 block">
                                                No disponible en {actionDay.isLibranza ? 'Libranza' : actionDay.isVacacion ? 'Vacaciones' : actionDay.isQuincena ? 'Quincena' : 'este día'}
                                            </span>
                                        </div>
                                        <Lock className="w-5 h-5 text-slate-600" />
                                    </button>
                                ) : (apBalance?.desglose?.ajuste_jornada?.disponibles <= 0) ? (
                                    <button disabled className="w-full bg-slate-800/30 border border-slate-700/30 p-6 rounded-3xl text-left flex justify-between items-center opacity-50 cursor-not-allowed select-none">
                                        <div>
                                            <span className="font-black text-slate-500 text-sm uppercase tracking-widest block">Ajuste de Jornada</span>
                                            <span className="text-[10px] text-amber-500 font-bold uppercase tracking-wider mt-1 block">
                                                Sin saldo disponible
                                            </span>
                                        </div>
                                        <AlertCircle className="w-5 h-5 text-amber-600" />
                                    </button>
                                ) : (
                                    <button onClick={() => handleConfirmQuickAction(actionDay.dateStr, 'ajuste_jornada')} className="w-full bg-amber-500/10 border border-amber-500/20 p-6 rounded-3xl text-left flex justify-between items-center group transition-all hover:bg-amber-500/20">
                                        <span className="font-black text-amber-500 text-sm uppercase tracking-widest">Ajuste de Jornada</span>
                                        <ChevronRight className="w-5 h-5 text-amber-500 group-hover:translate-x-1 transition-transform" />
                                    </button>
                                )}
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Custom Confirmation Modal */}
            <AnimatePresence>
                {confirmModal.isOpen && (
                    <div className="fixed inset-0 z-[200] flex items-center justify-center p-6">
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))} />
                        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} className="bg-[var(--bg-secondary)] border border-[var(--glass-border)] w-full max-w-sm rounded-[32px] p-8 relative z-50 shadow-2xl text-center">
                            <div className={`w-16 h-16 rounded-2xl mx-auto mb-6 flex items-center justify-center ${confirmModal.type === 'amber' ? 'bg-amber-500/10 text-amber-500' : 'bg-cyan-500/10 text-cyan-500'}`}>
                                <AlertCircle className="w-8 h-8" />
                            </div>
                            <h3 className="text-xl font-black text-[var(--text-primary)] mb-2">{confirmModal.title}</h3>
                            <p className="text-[var(--text-secondary)] text-sm mb-8 leading-relaxed font-medium">{confirmModal.message}</p>
                            <div className="grid grid-cols-2 gap-3">
                                <button onClick={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))} className="bg-[var(--glass-bg)] hover:bg-[var(--glass-border)] py-4 rounded-2xl font-black text-[var(--text-primary)] text-xs uppercase tracking-widest transition-colors">Cancelar</button>
                                <button onClick={confirmModal.onConfirm} className={`py-4 rounded-2xl font-black text-white text-xs uppercase tracking-widest shadow-lg transition-all ${confirmModal.type === 'amber' ? 'bg-amber-500 shadow-amber-500/20' : 'bg-cyan-500 shadow-cyan-500/20'}`}>Confirmar</button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            <OnboardingTour steps={TOUR_STEPS} isOpen={showTour} onComplete={() => setShowTour(false)} />

            {/* Manual Painter Palette */}
            <ManualPainterPalette
                isOpen={showPainterPalette}
                onClose={() => { setShowPainterPalette(false); setPainterRange({ start: null, end: null }); }}
                onSelect={handlePainterSelect}
                rangeLabel={painterRange.start && painterRange.end ? `${parseInt(painterRange.start.split('-')[2])} al ${parseInt(painterRange.end.split('-')[2])} de ${MONTHS[currentMonth]}` : ''}
            />

            {/* Global Saving Loader for Manual Painter */}
            <AnimatePresence>
                {isSavingManual && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[300] bg-black/80 backdrop-blur-md flex flex-col items-center justify-center p-6"
                    >
                        <div className="relative">
                            <motion.div 
                                animate={{ rotate: 360 }}
                                transition={{ repeat: Infinity, duration: 1.5, ease: "linear" }}
                                className="w-16 h-16 rounded-full border-4 border-cyan-500/10 border-t-cyan-500"
                            />
                            <motion.div 
                                animate={{ scale: [1, 1.2, 1] }}
                                transition={{ repeat: Infinity, duration: 2 }}
                                className="absolute inset-0 flex items-center justify-center"
                            >
                                <Zap className="w-6 h-6 text-cyan-500" />
                            </motion.div>
                        </div>
                        <p className="mt-6 text-xl font-black text-white uppercase tracking-[0.2em]">Guardando cambios</p>
                        <p className="mt-2 text-cyan-400 font-bold text-xs uppercase tracking-widest animate-pulse">Sincronizando con el servidor...</p>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default UserDashboard;
